import { FC, useMemo } from 'react'
import { Box } from '@mui/material'
import { ResponsiveLine } from '@nivo/line'
import { xxllncColor } from '../../layout/themes'

interface PivotRow {
  leeftijd: string | number
  [year: string]: number | string | null | undefined
}

interface Props {
  columns: string[]
  data: PivotRow[]
  geslachtFilter: string
}

const HistorischeDataChart: FC<Props> = ({ columns, data, geslachtFilter }) => {
  const yearColumns = columns.filter((c) => /^\d{4}$/.test(c))

  const chartData = useMemo(() => {
    const totalRow = data.find((r) => r.leeftijd === 'Totaal')
    if (!totalRow) return []

    const label =
      geslachtFilter === 'm'
        ? 'Mannen'
        : geslachtFilter === 'v'
          ? 'Vrouwen'
          : 'Totaal (alle rijen)'

    return [
      {
        id: label,
        data: yearColumns.map((year) => ({
          x: year,
          y: typeof totalRow[year] === 'number' ? (totalRow[year] as number) : 0,
        })),
      },
    ]
  }, [data, yearColumns, geslachtFilter])

  if (chartData.length === 0) return null

  return (
    <Box sx={{ height: '100%', minHeight: 320, p: 2 }}>
      <ResponsiveLine
        data={chartData}
        margin={{ top: 30, right: 110, bottom: 60, left: 70 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
        curve="monotoneX"
        axisBottom={{
          tickSize: 5,
          tickPadding: 8,
          legend: 'Jaar',
          legendOffset: 44,
          legendPosition: 'middle',
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 8,
          format: (value: number) => value.toLocaleString('nl-NL'),
        }}
        enableGridX={false}
        enablePoints
        pointSize={8}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointColor={{ theme: 'background' }}
        colors={[xxllncColor.yellow100]}
        lineWidth={3}
        enableArea
        areaOpacity={0.18}
        useMesh
        legends={[
          {
            anchor: 'right',
            direction: 'column',
            translateX: 100,
            itemWidth: 90,
            itemHeight: 18,
            symbolShape: 'square',
            symbolSize: 12,
          },
        ]}
        theme={{
          axis: {
            ticks: { text: { fontSize: 11, fill: xxllncColor.dark60 } },
            legend: {
              text: { fontSize: 12, fill: xxllncColor.dark100, fontWeight: 600 },
            },
          },
          grid: { line: { stroke: xxllncColor.opDark10 } },
          legends: { text: { fontSize: 12, fill: xxllncColor.dark100 } },
        }}
      />
    </Box>
  )
}

export default HistorischeDataChart
